import { Box, BoxProps, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useRef } from "react";

export interface DynamicBoxProps extends BoxProps {
  bottomPadding?: number;
  watchElement?: HTMLElement | null;
  disabled?: boolean;
  recalcKey?: any;
}

function debounce(fn: () => void, delay: number) {
  let timer: any;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}

const DynamicBox: React.FC<DynamicBoxProps> = ({
  children,
  bottomPadding = 24,
  watchElement = null,
  disabled,
  sx,
  recalcKey,
  ...rest
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const calculateHeight = () => {
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      const newHeight = window.innerHeight - rect.top - bottomPadding;
      boxRef.current.style.height = `${newHeight}px`;
      boxRef.current.style.maxHeight = `${newHeight}px`;
    }
  };

  // Debounced version for observer and resize
  const debouncedCalculateHeight = debounce(calculateHeight, 200);

  useEffect(() => {
    if (disabled || isMobile) return;
    calculateHeight();

    let parentObserver: ResizeObserver | undefined;
    let watchObserver: ResizeObserver | undefined;

    // Observe parent element
    if (boxRef.current && boxRef.current.parentElement) {
      parentObserver = new ResizeObserver(debouncedCalculateHeight);
      parentObserver.observe(boxRef.current.parentElement);
    }

    // Observe external element if provided
    if (watchElement) {
      watchObserver = new ResizeObserver(debouncedCalculateHeight);
      watchObserver.observe(watchElement);
    }

    window.addEventListener("resize", debouncedCalculateHeight);

    return () => {
      if (parentObserver) parentObserver.disconnect();
      if (watchObserver) watchObserver.disconnect();
      window.removeEventListener("resize", debouncedCalculateHeight);
    };
    // eslint-disable-next-line
  }, [watchElement, bottomPadding, disabled, isMobile]);

  // recalcKey triggers recalculation
  useEffect(() => {
    if (disabled || isMobile) return;
    calculateHeight();
  }, [recalcKey, disabled, bottomPadding, isMobile]);

  useEffect(() => {
    if (disabled || isMobile) return;
    const mainElement = document.body;
    if (mainElement) {
      mainElement.style.overflow = "hidden";
    }
    return () => {
      if (mainElement) {
        mainElement.style.overflow = "auto";
      }
    };
  }, [disabled, isMobile]);

  return (
    <Box
      ref={boxRef}
      sx={
        disabled || isMobile
          ? sx
          : {
              overflow: "auto",
              backgroundColor: "#f0f0f0",
              transition: "height 0.2s ease",
              ...sx,
            }
      }
      {...rest}
    >
      {children}
    </Box>
  );
};

export default DynamicBox;
